/**
 * Arena — real-time bullet-hell mini-game scene.
 *
 * Triggered when the player declines the forest crone's thread. Three
 * stages of escalating fly-dodge difficulty, A/D (or ←/→) to move,
 * collisions burn lives via the engine's existing loseLife(). If
 * lives hit 0 mid-arena, the engine fires the failure ending
 * automatically; this runner just resolves with `false` and lets
 * the engine's normal flow take over.
 *
 * Independent of Three.js / parser pipeline — pure DOM + rAF for
 * portability and to keep the stage rendering separate from the
 * paper-theater scene below.
 */

type Engine = {
  loseLife(): void;
  readonly lives: number;
};

type FlyMotion = 'straight' | 'sine' | 'random';

type StageConfig = {
  banner: string;
  durationMs: number;
  spawnIntervalMs: { min: number; max: number };
  flySpeedPx: { min: number; max: number };
  flyMotion: FlyMotion;
  flySize: { min: number; max: number };
  flyTypes: number[]; // 1, 2, 3 → /img/fly{n}.png
  /** Optional initial pause before flies start spawning, so the player
   *  has a beat to read the banner and find their footing. */
  warmupMs?: number;
};

const STAGES: StageConfig[] = [
  {
    // Stage 2 (now first): introduces sine-wave motion at a gentle pace.
    // The earlier "I — the swarm begins" tutorial wave was cut so the
    // arena drops the player straight into the wider-skies pace.
    banner: 'II — wider skies',
    durationMs: 13000,
    spawnIntervalMs: { min: 750, max: 1400 },
    flySpeedPx: { min: 220, max: 380 },
    flyMotion: 'sine',
    flySize: { min: 50, max: 80 },
    flyTypes: [1, 2, 3],
    warmupMs: 700,
  },
  {
    // Stage 3: boss-rush finale, intentionally hard.
    banner: 'III — the hollow hour',
    durationMs: 16000,
    spawnIntervalMs: { min: 200, max: 500 },
    flySpeedPx: { min: 380, max: 600 },
    flyMotion: 'random',
    flySize: { min: 50, max: 100 },
    flyTypes: [1, 2, 3],
    warmupMs: 500,
  },
];

// Player size mirrors the in-play 'child' sprite: greyman.png at
// roughly the same on-screen footprint as Three.js renders it in
// other rooms. Bigger than the first pass, and dropped much lower
// so the feet land on the painted stage floor instead of hovering
// in mid-air.
const PLAYER_W = 280;
const PLAYER_H = 420;
/** Pixels above the bottom of the viewport where the player's feet
 *  rest. Tuned so they sit roughly on the painted stage floor edge
 *  (the seam between the floor PNG and the backdrop). Stage
 *  variation: bump higher if the floor reads too low, lower if the
 *  player overlaps the audience silhouettes. */
const PLAYER_BOTTOM_OFFSET = 80;
/** Hitbox shrink — visual sprite is bigger than the actual collision box. */
const PLAYER_HIT_PAD_X = 80;
const PLAYER_HIT_PAD_Y = 100;
const PLAYER_SPEED_PX = 720;

const INVULN_MS = 900;

/** Dash burst — the player gets a short, fast lunge with i-frames.
 *  Distance ≈ DASH_SPEED_PX * (DASH_DURATION_MS/1000) ≈ 400px, roughly
 *  one-and-a-half body widths, so it actually clears tight clusters
 *  instead of just nudging out of them. Cooldown is just long enough
 *  that you can't spam through stage 3 — you have to time it. */
const DASH_SPEED_PX = 2200;
const DASH_DURATION_MS = 180;
const DASH_COOLDOWN_MS = 700;
/** A small tail of invuln after the dash ends so a fly that scrapes
 *  the player on the very last frame of the burst doesn't punish the
 *  panic-button use case. */
const DASH_INVULN_TAIL_MS = 80;

/** Jump physics — single-press hop, no double jump. JUMP_VELOCITY ÷
 *  GRAVITY_PX gives airtime each direction; the apex height is
 *  v²/(2·g) ≈ 200px, enough to clear a small fly column passing
 *  through the puppet's chest but not a wall of flies. No air-control
 *  on the vertical axis — once committed, the arc plays out. */
const JUMP_VELOCITY_PX = 1100; // initial upward velocity
const GRAVITY_PX = 3000;       // downward acceleration

/** Boss finale — fires once after the three fly stages survive. The
 *  boss has no HP, just a survival timer; the displayed HP bar ticks
 *  down with elapsed time, framed as the witch losing power for as
 *  long as the player keeps dodging. Two attack patterns alternate —
 *  one rewards the dash, one rewards the jump. */
const BOSS_DURATION_MS         = 18000;
const BOSS_BREATH_MS           = 700;
/** Volley: 3 telegraphed columns. Player moves/dashes out of them. */
const BOSS_VOLLEY_COLS         = 3;
const BOSS_VOLLEY_TELL_MS      = 700;
const BOSS_VOLLEY_FIRE_MS      = 1500;
const BOSS_VOLLEY_FLY_SIZE     = 70;
const BOSS_VOLLEY_FLY_SPEED    = 700;
/** Sweep: the puppeteer rises from beneath the stage floor and
 *  slides across, head peeking above the floor edge. Player must
 *  jump over the peeking head. The sprite lives at z-1 (behind the
 *  floor at z-2) so the floor visually masks the body — only the
 *  head/upper body shows above the floor edge.
 *
 *  Constants split visual vs hitbox:
 *    - SPRITE_W: width of the rendered <img>
 *    - HITBOX_W / HITBOX_H: collision rect, centered horizontally on
 *      the sprite, sitting at SWEEP_PEEK_FROM_FLOOR above the bottom.
 *  Hitbox is narrower than the sprite so the soft sides of the
 *  puppeteer's silhouette don't punish near-misses. */
const BOSS_SWEEP_TELL_MS         = 800;
/** Sprite is 1263×832 (≈3:2 portrait). 380px wide → ~250px tall. */
const BOSS_SWEEP_SPRITE_W        = 380;
const BOSS_SWEEP_HITBOX_W        = 200;
const BOSS_SWEEP_HITBOX_H        = 100;
const BOSS_SWEEP_SPEED           = 1300;
/** Y of the hitbox CENTER, in pixels above the bottom of the
 *  viewport. With h=100 the rect spans 80→180 from bottom; jump
 *  apex is ~200px so a ~half-apex jump (jumpY > 120) clears
 *  cleanly. Bottom edge at 80 lines up exactly with the player's
 *  feet so a grounded player gets hit. */
const BOSS_SWEEP_PEEK_FROM_FLOOR = 130;
/** How far below its peeking position the puppeteer starts before
 *  rising up — purely visual flair. The hitbox follows the rise so
 *  the player can't be punished while it's still climbing. */
const BOSS_SWEEP_RISE_OFFSET_PX  = 220;
const BOSS_SWEEP_RISE_DURATION_S = 0.3;

/** Pixel-precise alpha mask of the player sprite. Loaded once at the
 *  start of run() — greyman.png is decoded into a low-res grid (each
 *  cell = "is the source pixel here non-transparent?"), then sampled
 *  during collision checks. Reads collisions only against the actual
 *  silhouette, not the transparent padding around it. */
class PlayerMask {
  constructor(
    private grid: Uint8Array,
    private gridW: number,
    private gridH: number,
  ) {}

  static async load(url: string, gridW = 96, gridH = 144): Promise<PlayerMask | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = gridW;
          canvas.height = gridH;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(img, 0, 0, gridW, gridH);
          const data = ctx.getImageData(0, 0, gridW, gridH).data;
          const grid = new Uint8Array(gridW * gridH);
          for (let i = 0, p = 0; i < data.length; i += 4, p++) {
            // Alpha threshold — feathered pixels under ~32/255 count
            // as "no" so a fly clipping the soft edge of the figure
            // doesn't register as a hit.
            grid[p] = data[i + 3] > 32 ? 1 : 0;
          }
          resolve(new PlayerMask(grid, gridW, gridH));
        } catch (err) {
          console.warn('[arena] mask decode failed', err);
          resolve(null);
        }
      };
      img.onerror = () => { console.warn(`[arena] mask load failed: ${url}`); resolve(null); };
      img.src = url;
    });
  }

  /** Test a screen-space point against the player's silhouette,
   *  given the player's bounding-box position + size. Returns true
   *  iff the point falls within the box AND the source pixel at
   *  that location was non-transparent. */
  isOpaqueAt(
    px: number, py: number,
    boxLeft: number, boxTop: number,
    boxW: number, boxH: number,
  ): boolean {
    const u = (px - boxLeft) / boxW;
    const v = (py - boxTop) / boxH;
    if (u < 0 || u >= 1 || v < 0 || v >= 1) return false;
    const gx = Math.floor(u * this.gridW);
    const gy = Math.floor(v * this.gridH);
    return this.grid[gy * this.gridW + gx] === 1;
  }
}

interface Fly {
  el: HTMLImageElement;
  x: number; // center
  y: number;
  vx: number;
  vy: number;
  size: number;
  spinDir: 1 | -1;
  spinRate: number; // deg/sec
  baseX: number;
  motion: FlyMotion;
  age: number;
  dead: boolean;
}

export class ArenaRunner {
  private root: HTMLElement;
  /** Separate DOM layer for the flies, inserted into the page BEFORE
   *  the .back-curtains element so flies render behind the back-
   *  stage drapes (drop-in-from-behind-the-curtain effect). */
  private flyLayer!: HTMLElement;
  /** Dedicated layer for the player at z-3 — between the floor (z-2)
   *  and the temporarily-raised back-curtains (z-4 while arena runs).
   *  Lets the player stand IN FRONT of the floor but BEHIND the
   *  side drapes, matching the rest of the paper-theatre cast except
   *  for the arena's curtain override. Separate from .arena (z-4) so
   *  banner / hint / flash / progress UI stay above back-curtains. */
  private playerLayer!: HTMLElement;
  /** Reference to the page's .back-curtains element so we can bump
   *  its z-index AND clip-mask its bottom lip during the arena, then
   *  restore both on cleanup. */
  private backCurtainsEl: HTMLElement | null = null;
  private savedBackCurtainsZ = '';
  private savedBackCurtainsClip = '';
  /** The player wrap div — receives the X/Y position transform.
   *  Its child <img> runs an independent idle-sway CSS animation. */
  private playerEl: HTMLElement;
  private bannerEl: HTMLElement;
  private flashEl: HTMLElement;
  private progressEl: HTMLElement;
  private hintEl: HTMLElement;
  private flies: Fly[] = [];
  private playerX = 0;
  private viewportW = 0;
  private viewportH = 0;
  private leftHeld = false;
  private rightHeld = false;
  /** Last horizontal direction the player committed to. Used as the
   *  dash direction when the player taps Shift without holding a key
   *  (otherwise standing-still dashes would always fire right). */
  private facing: -1 | 1 = 1;
  /** performance.now() timestamps for dash state. dashEndAt = when
   *  the burst stops; dashReadyAt = when the next dash can fire. */
  private dashEndAt = 0;
  private dashReadyAt = 0;
  private dashDir: -1 | 1 = 1;
  /** Vertical jump state — jumpY = pixels above the ground line
   *  (PLAYER_BOTTOM_OFFSET), jumpVy = current vertical velocity.
   *  Both 0 ⇒ grounded. Single hop per press; no double-jump. */
  private jumpY = 0;
  private jumpVy = 0;
  /** Boss DOM nodes — created on demand for the finale, removed on
   *  cleanup. The sprite lives in flyLayer (z-1) so the floor (z-2)
   *  masks its lower body, making the puppeteer read as "peeking from
   *  behind the stage floor". The HP bar lives in .arena root (z-4)
   *  so it stays on top of everything regardless of stage layering. */
  private bossSpriteEl: HTMLElement | null = null;
  private bossHpWrapEl: HTMLElement | null = null;
  private bossHpEl: HTMLElement | null = null;
  /** Active sweep — the puppeteer sprite peeking from below the
   *  floor. Null when not on screen. `riseT` ramps 0→1 over the
   *  rise duration, used to lerp the visual position upward AND to
   *  shift the hitbox so the player isn't hit while the sprite is
   *  still climbing. */
  private beam: {
    x: number;
    vx: number;
    /** fallback hitbox width (used if mask not loaded) */ w: number;
    /** fallback hitbox height (used if mask not loaded) */ h: number;
    /** sprite visual width (px) */ spriteW: number;
    /** sprite visual height (px) — equals spriteW for the 1:1 stone sprite */ spriteH: number;
    /** y center of the fallback hitbox at full rise (from top of viewport) */ y: number;
    /** rise progress 0..1 */ riseT: number;
    el: HTMLElement;
  } | null = null;
  /** Audience-overlay handle + saved state — the boss phase lowers
   *  the audience silhouettes (8vh down) so the floor edge is visible
   *  for the puppeteer's peek-out. Restored on cleanup. */
  private audienceEl: HTMLElement | null = null;
  private audienceWasLowered = false;
  private active = false;
  private paused = false;
  private invulnUntil = 0;
  private rafId: number | null = null;
  /** Pixel-precise silhouette mask for the player sprite. Null until
   *  loaded (start of run); collision falls back to bounding-box
   *  while it's still null. */
  private playerMask: PlayerMask | null = null;
  /** Pixel-precise mask for the sweep hazard sprite (currently
   *  stone1.png — a square PNG with the rock centered and
   *  transparent corners). Used so collision follows the visible
   *  rock pixels instead of the full square bounding box. Falls
   *  back to the inner BOSS_SWEEP_HITBOX_W/H rect while loading. */
  private sweepMask: PlayerMask | null = null;

  constructor(private engine: Engine) {
    this.root = this.buildDOM();
    // Player div is created inside .arena-fly-layer in run() so it
    // renders behind the back curtains; placeholder assigned here is
    // overwritten before any code touches it.
    this.playerEl = document.createElement('div');
    this.bannerEl = this.root.querySelector('.arena-banner')!;
    this.flashEl = this.root.querySelector('.arena-flash')!;
    this.progressEl = this.root.querySelector('.arena-progress-fill')!;
    this.hintEl = this.root.querySelector('.arena-hint')!;
  }

  /** Run the full arena. Resolves true if the player survived all
   *  stages, false if they ran out of lives. */
  async run(): Promise<boolean> {
    // Kick off the mask load before the first stage. We don't block
    // on it — if it's still loading when stage 1 starts, collision
    // falls back to bounding-box. greyman.png is small (KB) and
    // decoding into a 96×144 grid is fast, so it's almost always
    // ready by the time the first fly enters the screen.
    PlayerMask.load('/img/greyman.png').then(m => { this.playerMask = m; });
    // Same alpha-mask trick for the sweep hazard so the hitbox follows
    // the visible rock instead of the full 500×500 square.
    PlayerMask.load('/img/stone1.png').then(m => { this.sweepMask = m; });

    document.body.appendChild(this.root);
    // Insert a separate fly layer BEFORE the .back-curtains element
    // in the DOM. With both at z-1, source order makes the fly layer
    // render behind back-curtains but in front of #stage-bg, so
    // flies appear to descend from behind the back drapes.
    this.flyLayer = document.createElement('div');
    this.flyLayer.className = 'arena-fly-layer';
    const backCurtains = document.querySelector('.back-curtains');
    if (backCurtains?.parentNode) {
      backCurtains.parentNode.insertBefore(this.flyLayer, backCurtains);
    } else {
      // Fallback — no back-curtains element to anchor to. Append to
      // body and accept that flies will render in front; better than
      // not rendering at all.
      document.body.appendChild(this.flyLayer);
    }
    // Player lives in its own .arena-player-layer at z-3 — above the
    // floor (z-2) so the puppet stands ON the painted floor, and
    // below the back-curtains (which we bump to z-4 just below) so
    // the side drapes obscure the player when they drift to the
    // edges of the stage. Separate from .arena root (z-4) so the
    // banner / progress bar / flash / hint UI all still sit on top
    // of the back-curtains.
    //
    // CRITICAL: insert into #frame, NOT body. #frame is position:fixed
    // which creates its own stacking context; bumping back-curtains to
    // z-4 only affects stacking *inside* #frame. If the player layer
    // sits in body's stacking context instead, it renders on top of
    // EVERYTHING in #frame regardless of inner z-indexes — which is
    // why the player kept showing in front of the curtains. Living
    // inside #frame puts the player in the same stacking context as
    // back-curtains so the inner z-indexes actually compete.
    this.playerLayer = document.createElement('div');
    this.playerLayer.className = 'arena-player-layer';
    const frameEl = document.getElementById('frame') ?? document.body;
    frameEl.appendChild(this.playerLayer);
    this.playerEl = document.createElement('div');
    this.playerEl.className = 'arena-player';
    this.playerEl.innerHTML = '<img src="/img/greyman.png" alt="" draggable="false" />';
    this.playerLayer.appendChild(this.playerEl);
    // Bump back-curtains above the player layer for the duration of
    // the arena, AND clip out the wooden-lip portion at the bottom of
    // the PNG. The lip is baked into the image around the bottom 28%
    // and would otherwise sit IN FRONT of the player's feet at the
    // bumped z-index, reading as "the player is behind the floor".
    // Clipping leaves only the upper/side drapes raised so the player
    // stands cleanly on the actual .floor-overlay (z-2) and is
    // occluded by the drapes only at the edges of the proscenium.
    // Restored in cleanup() so the rest of the play keeps the
    // original "cast in front of drapes" stacking + un-clipped image.
    this.backCurtainsEl = document.querySelector('.back-curtains');
    if (this.backCurtainsEl) {
      this.savedBackCurtainsZ = this.backCurtainsEl.style.zIndex;
      this.savedBackCurtainsClip = this.backCurtainsEl.style.clipPath;
      this.backCurtainsEl.style.zIndex = '4';
      this.backCurtainsEl.style.clipPath = 'inset(0 0 28% 0)';
    }
    // Drop the `hidden` class FIRST — `.arena.hidden { display:none }`
    // beats `.arena.visible { opacity:1 }`, so leaving `hidden` on
    // makes the entire arena invisible. The reflow + `visible` then
    // triggers the opacity fade-in.
    this.root.classList.remove('hidden');
    void this.root.offsetWidth;
    this.root.classList.add('visible');
    this.viewportW = window.innerWidth;
    this.viewportH = window.innerHeight;
    this.playerX = this.viewportW / 2;
    this.installInput();
    this.placePlayer();
    this.active = true;
    this.showHint();

    let won = true;
    try {
      for (let i = 0; i < STAGES.length; i++) {
        if (!this.active || this.engine.lives <= 0) { won = false; break; }
        await this.showBanner(STAGES[i].banner);
        const survived = await this.runStage(STAGES[i]);
        if (!survived) { won = false; break; }
        // Brief breath between stages.
        await this.wait(800);
      }
      // Boss finale — fires only if the player cleared all fly stages.
      if (won && this.active && this.engine.lives > 0) {
        await this.showBanner("IV — the crone's wrath");
        const survived = await this.runBoss();
        if (!survived) won = false;
      }
      if (won) await this.showBanner('— the witch falls —', 1800);
    } finally {
      this.cleanup();
    }
    return won;
  }

  // ── DOM ─────────────────────────────────────────────────────────

  private buildDOM(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'arena hidden';
    // The player div is created separately in run() and inserted
    // into .arena-fly-layer (behind the back curtains) — not here.
    // .arena root is just for the in-front-of-curtains UI: the
    // screen flash, the stage progress bar, the input hint, and
    // the stage banner.
    root.innerHTML = `
      <div class="arena-flash"></div>
      <div class="arena-progress"><div class="arena-progress-fill"></div></div>
      <div class="arena-hint">
        <kbd>A</kbd> / <kbd>D</kbd> &nbsp;move&nbsp;&nbsp; <kbd>Space</kbd> &nbsp;jump&nbsp;&nbsp; <kbd>Shift</kbd> &nbsp;dash&nbsp;&nbsp; <span>dodge the swarm</span>
      </div>
      <div class="arena-banner hidden"></div>
    `;
    return root;
  }

  private placePlayer() {
    const x = this.playerX - PLAYER_W / 2;
    // jumpY pushes the puppet UP from the ground line — subtracted
    // from y because page coordinates increase downward.
    const y = this.viewportH - PLAYER_H - PLAYER_BOTTOM_OFFSET - this.jumpY;
    this.playerEl.style.transform = `translate(${x}px, ${y}px)`;
  }

  private showHint() {
    this.hintEl.classList.add('visible');
    setTimeout(() => this.hintEl.classList.remove('visible'), 4500);
  }

  // ── Input ───────────────────────────────────────────────────────

  private installInput() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.onFocus);
  }
  private removeInput() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.onFocus);
  }
  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'KeyA' || e.code === 'ArrowLeft')  { this.leftHeld = true;  this.facing = -1; }
    if (e.code === 'KeyD' || e.code === 'ArrowRight') { this.rightHeld = true; this.facing =  1; }
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      if (!e.repeat) this.tryDash();
      e.preventDefault();
    }
    if (e.code === 'Space' || e.code === 'KeyW' || e.code === 'ArrowUp') {
      // Block Space from scrolling the page and autorepeat from
      // multi-firing the jump while held.
      if (!e.repeat) this.tryJump();
      e.preventDefault();
    }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === 'KeyA' || e.code === 'ArrowLeft')  this.leftHeld = false;
    if (e.code === 'KeyD' || e.code === 'ArrowRight') this.rightHeld = false;
  };

  private tryDash() {
    const now = performance.now();
    if (now < this.dashReadyAt) return;
    if (now < this.dashEndAt)   return; // already dashing
    // Held key wins; otherwise dash in last-committed facing.
    this.dashDir = this.leftHeld ? -1 : this.rightHeld ? 1 : this.facing;
    this.dashEndAt   = now + DASH_DURATION_MS;
    this.dashReadyAt = now + DASH_COOLDOWN_MS;
    // I-frames span the whole burst plus a small tail.
    this.invulnUntil = Math.max(this.invulnUntil, this.dashEndAt + DASH_INVULN_TAIL_MS);
    this.playerEl.style.setProperty('--dash-dir', String(this.dashDir));
    // Restart the dash animation by toggling the class off → reflow → on.
    this.playerEl.classList.remove('dashing');
    void this.playerEl.offsetWidth;
    this.playerEl.classList.add('dashing');
  }

  private tryJump() {
    // Grounded check — only jump from the floor, no double-jump or
    // mid-air kick. jumpY stays exactly 0 while grounded.
    if (this.jumpY > 0 || this.jumpVy !== 0) return;
    this.jumpVy = JUMP_VELOCITY_PX;
    this.playerEl.classList.add('jumping');
  }
  private onBlur = () => { this.paused = true; this.leftHeld = false; this.rightHeld = false; };
  private onFocus = () => { this.paused = false; };

  // ── Stage banner ────────────────────────────────────────────────

  private async showBanner(text: string, holdMs = 1500): Promise<void> {
    this.bannerEl.textContent = text;
    this.bannerEl.classList.remove('hidden');
    void this.bannerEl.offsetWidth;
    this.bannerEl.classList.add('visible');
    await this.wait(holdMs);
    this.bannerEl.classList.remove('visible');
    await this.wait(420);
    this.bannerEl.classList.add('hidden');
  }

  // ── Stage loop ──────────────────────────────────────────────────

  /** Returns false iff the player died (lives hit 0). */
  private runStage(stage: StageConfig): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      const startTime = performance.now();
      let nextSpawn = startTime + (stage.warmupMs ?? 0);
      let lastTick = startTime;

      const tick = (now: number) => {
        if (!this.active) {
          this.rafId = null;
          resolve(this.engine.lives > 0);
          return;
        }
        if (this.paused) {
          lastTick = now;
          this.rafId = requestAnimationFrame(tick);
          return;
        }

        const dt = Math.min(0.08, (now - lastTick) / 1000); // clamp big gaps
        lastTick = now;
        const elapsed = now - startTime;
        const stageProgress = Math.min(1, elapsed / stage.durationMs);
        this.progressEl.style.width = `${stageProgress * 100}%`;

        // End of stage?
        if (elapsed >= stage.durationMs) {
          this.rafId = null;
          this.clearAllFlies();
          this.progressEl.style.width = '100%';
          resolve(true);
          return;
        }

        // Spawn?
        if (now >= nextSpawn) {
          this.spawnFly(stage);
          const interval = randInt(stage.spawnIntervalMs.min, stage.spawnIntervalMs.max);
          nextSpawn = now + interval;
        }

        this.updatePlayer(dt);
        this.updateFlies(dt);

        if (now >= this.invulnUntil) {
          if (this.checkPlayerHit(now)) {
            if (this.engine.lives <= 0) {
              this.rafId = null;
              resolve(false);
              return;
            }
          }
        } else {
          this.playerEl.classList.add('invuln');
        }
        if (now >= this.invulnUntil) this.playerEl.classList.remove('invuln');

        // GC dead flies.
        if (this.flies.some(f => f.dead)) this.flies = this.flies.filter(f => !f.dead);

        this.rafId = requestAnimationFrame(tick);
      };

      this.rafId = requestAnimationFrame(tick);
    });
  }

  // ── Boss finale ─────────────────────────────────────────────────
  //
  // After the player clears all three fly stages, a witch boss perches
  // at the top center of the stage and cycles between two telegraphed
  // attacks: a 3-column volley (rewards horizontal movement / dash)
  // and a floor-grazing sweep beam (rewards jump). Defeat condition
  // is timer-based — the displayed HP bar drains over BOSS_DURATION_MS,
  // framed as the witch losing power for as long as the player keeps
  // dodging. Phase machine lives in a single rAF tick (mirrors
  // runStage) so movement/collision/animation share a clock with the
  // attack scheduler.

  private runBoss(): Promise<boolean> {
    type Phase =
      | { kind: 'breath'; until: number }
      | { kind: 'volley_tell'; until: number; xs: number[]; tells: HTMLElement[] }
      | { kind: 'volley_fire'; until: number }
      | { kind: 'sweep_tell'; until: number; dir: -1 | 1; tell: HTMLElement }
      | { kind: 'sweep_active'; until: number };

    return new Promise<boolean>(resolve => {
      this.createBoss();
      // Drop the audience silhouettes 8vh during the boss phase so the
      // floor edge is visible and the puppeteer's peek-out reads. CSS
      // class `.lowered` is the existing knob that handles this; we
      // restore the previous state in cleanup() so a user-set lower-
      // audience preference isn't clobbered.
      this.audienceEl = document.querySelector('.audience-overlay');
      if (this.audienceEl) {
        this.audienceWasLowered = this.audienceEl.classList.contains('lowered');
        this.audienceEl.classList.add('lowered');
      }
      const startTime = performance.now();
      let lastTick = startTime;
      // Initial breath so the player has a beat to read the banner
      // before the first telegraph appears.
      let phase: Phase = { kind: 'breath', until: startTime + BOSS_BREATH_MS };
      let attackIdx = 0;

      const tick = (now: number) => {
        if (!this.active) {
          this.rafId = null;
          resolve(this.engine.lives > 0);
          return;
        }
        if (this.paused) {
          lastTick = now;
          this.rafId = requestAnimationFrame(tick);
          return;
        }

        const dt = Math.min(0.08, (now - lastTick) / 1000);
        lastTick = now;
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / BOSS_DURATION_MS);
        this.progressEl.style.width = `${progress * 100}%`;
        if (this.bossHpEl) this.bossHpEl.style.width = `${(1 - progress) * 100}%`;

        // Boss defeated — survival timer expired. Clean up active
        // hazards, play the death animation, then resolve win.
        if (elapsed >= BOSS_DURATION_MS) {
          this.rafId = null;
          this.clearAllFlies();
          this.clearBeam();
          this.bossDeath().then(() => resolve(true));
          return;
        }

        // Phase progression — when the current phase's `until` hits,
        // step to the next via the state-machine table below.
        if (now >= phase.until) {
          phase = this.advanceBossPhase(phase, attackIdx, now);
          if (phase.kind === 'breath') attackIdx++;
        }

        this.updatePlayer(dt);
        this.updateFlies(dt);
        this.updateBeam(dt);

        // Collision — check fly hits AND beam hits. Beam hit drains a
        // life through the same loseLife() path so the heart UI fires.
        if (now >= this.invulnUntil) {
          let hit = this.checkPlayerHit(now);
          if (!hit && this.checkBeamHit()) {
            this.engine.loseLife();
            this.invulnUntil = now + INVULN_MS;
            this.flashScreen();
            this.flashPlayer();
            hit = true;
          }
          if (hit && this.engine.lives <= 0) {
            this.rafId = null;
            resolve(false);
            return;
          }
        }
        if (now >= this.invulnUntil) this.playerEl.classList.remove('invuln');
        else this.playerEl.classList.add('invuln');

        if (this.flies.some(f => f.dead)) this.flies = this.flies.filter(f => !f.dead);

        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    });
  }

  /** Phase transition. The order is fixed-alternating volley→sweep→…
   *  via attackIdx; this keeps the player learning each tell rather
   *  than fighting RNG mixups. */
  private advanceBossPhase(
    prev:
      | { kind: 'breath'; until: number }
      | { kind: 'volley_tell'; until: number; xs: number[]; tells: HTMLElement[] }
      | { kind: 'volley_fire'; until: number }
      | { kind: 'sweep_tell'; until: number; dir: -1 | 1; tell: HTMLElement }
      | { kind: 'sweep_active'; until: number },
    attackIdx: number,
    now: number,
  ):
    | { kind: 'breath'; until: number }
    | { kind: 'volley_tell'; until: number; xs: number[]; tells: HTMLElement[] }
    | { kind: 'volley_fire'; until: number }
    | { kind: 'sweep_tell'; until: number; dir: -1 | 1; tell: HTMLElement }
    | { kind: 'sweep_active'; until: number } {
    if (prev.kind === 'breath') {
      // Even attacks → volley, odd → sweep.
      if (attackIdx % 2 === 0) {
        const xs: number[] = [];
        const tells: HTMLElement[] = [];
        // Spread the columns evenly with random jitter so they don't
        // bunch up against the wall and become un-dodgeable.
        const margin = 90;
        const span = this.viewportW - margin * 2;
        for (let i = 0; i < BOSS_VOLLEY_COLS; i++) {
          const slot = (i + 0.5) * (span / BOSS_VOLLEY_COLS);
          const jitter = randInt(-30, 30);
          const x = margin + slot + jitter;
          xs.push(x);
          const el = document.createElement('div');
          el.className = 'arena-volley-tell';
          el.style.left = `${x - 30}px`;
          // Append to the .arena root (z-4) so the tells sit in front
          // of the bumped back-curtains and read clearly. The CSS
          // pins them at top:14vh, just below the proscenium top drape.
          this.root.appendChild(el);
          tells.push(el);
        }
        return { kind: 'volley_tell', until: now + BOSS_VOLLEY_TELL_MS, xs, tells };
      } else {
        const dir: -1 | 1 = Math.random() > 0.5 ? -1 : 1;
        const tell = document.createElement('div');
        tell.className = 'arena-sweep-tell';
        // Telegraph at the puppeteer's peek height — gives the player
        // a visual cue at exactly the line they need to clear with
        // the jump apex.
        const beamY = this.viewportH - BOSS_SWEEP_PEEK_FROM_FLOOR;
        tell.style.top = `${beamY - BOSS_SWEEP_HITBOX_H / 2}px`;
        tell.style.height = `${BOSS_SWEEP_HITBOX_H}px`;
        this.playerLayer.appendChild(tell);
        return { kind: 'sweep_tell', until: now + BOSS_SWEEP_TELL_MS, dir, tell };
      }
    }
    if (prev.kind === 'volley_tell') {
      for (const t of prev.tells) t.remove();
      for (const x of prev.xs) this.spawnVolleyFly(x);
      return { kind: 'volley_fire', until: now + BOSS_VOLLEY_FIRE_MS };
    }
    if (prev.kind === 'volley_fire') {
      return { kind: 'breath', until: now + BOSS_BREATH_MS };
    }
    if (prev.kind === 'sweep_tell') {
      prev.tell.remove();
      this.spawnBeam(prev.dir);
      // Active phase ends when beam fully crosses the screen.
      const crossMs = ((this.viewportW + BOSS_SWEEP_SPRITE_W) / BOSS_SWEEP_SPEED) * 1000 + 200;
      return { kind: 'sweep_active', until: now + crossMs };
    }
    /* prev.kind === 'sweep_active' */ {
      this.clearBeam();
      return { kind: 'breath', until: now + BOSS_BREATH_MS };
    }
  }

  private createBoss() {
    if (this.bossSpriteEl || this.bossHpWrapEl) return;
    // Sprite — appended to flyLayer (z-1 inside #frame) so the floor
    // (z-2) and audience (z-6) cover the lower body and only the head
    // peeks above the floor edge. CSS pins it to the bottom of the
    // viewport with a wide width so the puppeteer feels imposing.
    const sprite = document.createElement('img');
    sprite.className = 'arena-boss-sprite';
    sprite.src = '/img/Pupeteer No Background.png';
    sprite.draggable = false;
    sprite.alt = '';
    this.flyLayer.appendChild(sprite);
    this.bossSpriteEl = sprite;
    // HP bar — separate element in .arena root so it stays in front
    // of all stage / curtain / floor layers regardless of how the
    // sprite is layered.
    const hpWrap = document.createElement('div');
    hpWrap.className = 'arena-boss-hp-wrap';
    hpWrap.innerHTML = '<div class="arena-boss-hp"><div class="arena-boss-hp-fill"></div></div>';
    this.root.appendChild(hpWrap);
    this.bossHpWrapEl = hpWrap;
    this.bossHpEl = hpWrap.querySelector('.arena-boss-hp-fill') as HTMLElement;
  }

  private spawnVolleyFly(x: number) {
    const size = BOSS_VOLLEY_FLY_SIZE;
    const el = document.createElement('img');
    el.className = 'arena-fly';
    el.src = '/img/fly2.png';
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.draggable = false;
    el.alt = '';
    this.flyLayer.appendChild(el);
    this.flies.push({
      el,
      x,
      y: -size,
      vx: 0,
      vy: BOSS_VOLLEY_FLY_SPEED,
      size,
      spinDir: Math.random() > 0.5 ? 1 : -1,
      spinRate: 280,
      baseX: x,
      motion: 'straight',
      age: 0,
      dead: false,
    });
  }

  private spawnBeam(dir: -1 | 1) {
    if (this.beam) this.clearBeam();
    const spriteW = BOSS_SWEEP_SPRITE_W;
    // Stone is 1:1 (500×500 native), so rendered height = rendered
    // width. If the sweep sprite is ever swapped for a non-square
    // image, set spriteH explicitly here.
    const spriteH = BOSS_SWEEP_SPRITE_W;
    const w = BOSS_SWEEP_HITBOX_W;
    const h = BOSS_SWEEP_HITBOX_H;
    const y = this.viewportH - BOSS_SWEEP_PEEK_FROM_FLOOR;
    const startX = dir === 1 ? -spriteW : this.viewportW;
    const el = document.createElement('img');
    el.className = 'arena-sweep-puppeteer';
    el.src = '/img/stone1.png';
    el.draggable = false;
    el.alt = '';
    // Initial transform — start fully sunk below peek by RISE_OFFSET.
    el.style.transform = `translate(${startX}px, ${BOSS_SWEEP_RISE_OFFSET_PX}px)`;
    // Append to flyLayer (z-1) so the floor (z-2) masks the body and
    // only the peeking head reads above the floor edge.
    this.flyLayer.appendChild(el);
    this.beam = { x: startX, vx: BOSS_SWEEP_SPEED * dir, w, h, spriteW, spriteH, y, riseT: 0, el };
  }

  private updateBeam(dt: number) {
    if (!this.beam) return;
    this.beam.x += this.beam.vx * dt;
    this.beam.riseT = Math.min(1, this.beam.riseT + dt / BOSS_SWEEP_RISE_DURATION_S);
    const yOffset = (1 - this.beam.riseT) * BOSS_SWEEP_RISE_OFFSET_PX;
    this.beam.el.style.transform = `translate(${this.beam.x}px, ${yOffset}px)`;
  }

  private clearBeam() {
    if (!this.beam) return;
    this.beam.el.remove();
    this.beam = null;
  }

  /** Mask-on-mask collision between the sweep hazard sprite and the
   *  player. Each sprite is sampled at its own alpha mask so the
   *  collision follows the actual visible rock pixels (a circle in
   *  the middle of a 500×500 PNG with transparent corners) rather
   *  than the full square bbox. Falls back to the inner
   *  BOSS_SWEEP_HITBOX_W/H rect while the mask is still decoding. */
  private checkBeamHit(): boolean {
    if (!this.beam) return false;
    const yOffset = (1 - this.beam.riseT) * BOSS_SWEEP_RISE_OFFSET_PX;
    // Sprite rect in screen-from-top coords. Element is pinned with
    // CSS `bottom: 0`, so its bottom edge sits at y = viewportH;
    // translateY(yOffset) shifts that down by yOffset.
    const spriteBottom = this.viewportH + yOffset;
    const spriteTop    = spriteBottom - this.beam.spriteH;
    const spriteLeft   = this.beam.x;
    const spriteRight  = spriteLeft + this.beam.spriteW;
    // Player rect in screen-from-top coords.
    const playerLeft   = this.playerX - PLAYER_W / 2;
    const playerRight  = playerLeft + PLAYER_W;
    const playerTop    = this.viewportH - PLAYER_H - PLAYER_BOTTOM_OFFSET - this.jumpY;
    const playerBottom = playerTop + PLAYER_H;
    // Cheap bbox early-out — if the sprite squares don't overlap,
    // there's no chance of mask intersection.
    if (spriteRight <= playerLeft || spriteLeft >= playerRight) return false;
    if (spriteBottom <= playerTop || spriteTop >= playerBottom) return false;

    if (this.sweepMask && this.playerMask) {
      // Sample a 4×3 grid of points within the bbox overlap. Any
      // point where BOTH masks are opaque means the visible rock
      // and visible player silhouettes are touching.
      const ovL = Math.max(spriteLeft, playerLeft);
      const ovR = Math.min(spriteRight, playerRight);
      const ovT = Math.max(spriteTop, playerTop);
      const ovB = Math.min(spriteBottom, playerBottom);
      const cols = 4, rows = 3;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const sx = ovL + ((i + 0.5) / cols) * (ovR - ovL);
          const sy = ovT + ((j + 0.5) / rows) * (ovB - ovT);
          if (!this.sweepMask.isOpaqueAt(sx, sy, spriteLeft, spriteTop, this.beam.spriteW, this.beam.spriteH)) continue;
          if (!this.playerMask.isOpaqueAt(sx, sy, playerLeft, playerTop, PLAYER_W, PLAYER_H)) continue;
          return true;
        }
      }
      return false;
    }

    // Fallback while masks still loading — use the inner hitbox rect
    // so the player isn't penalized by the full-square bbox.
    const hbLeft   = this.beam.x + (this.beam.spriteW - this.beam.w) / 2;
    const hbRight  = hbLeft + this.beam.w;
    const hbTop    = this.beam.y - this.beam.h / 2 + yOffset;
    const hbBottom = this.beam.y + this.beam.h / 2 + yOffset;
    const tightL = playerLeft + 80;
    const tightR = playerRight - 80;
    if (hbRight <= tightL || hbLeft >= tightR) return false;
    if (hbBottom <= playerTop || hbTop >= playerBottom) return false;
    return true;
  }

  private async bossDeath(): Promise<void> {
    if (!this.bossSpriteEl) return;
    this.bossSpriteEl.classList.add('dying');
    if (this.bossHpEl) this.bossHpEl.style.width = '0%';
    await this.wait(900);
    this.bossSpriteEl.remove();
    this.bossSpriteEl = null;
    if (this.bossHpWrapEl) {
      this.bossHpWrapEl.remove();
      this.bossHpWrapEl = null;
    }
    this.bossHpEl = null;
  }

  private updatePlayer(dt: number) {
    const now = performance.now();
    const dashing = now < this.dashEndAt;
    if (dashing) {
      // Dash overrides walking velocity entirely — full burst speed
      // in the locked dashDir, regardless of which keys are held.
      this.playerX += DASH_SPEED_PX * dt * this.dashDir;
    } else {
      if (this.playerEl.classList.contains('dashing')) {
        this.playerEl.classList.remove('dashing');
      }
      if (this.leftHeld)  this.playerX -= PLAYER_SPEED_PX * dt;
      if (this.rightHeld) this.playerX += PLAYER_SPEED_PX * dt;
    }
    // Vertical physics — semi-implicit Euler. While airborne, integrate
    // velocity then position; clamp to ground and zero out velocity on
    // landing so the grounded-check in tryJump() reads true.
    if (this.jumpY > 0 || this.jumpVy !== 0) {
      this.jumpVy -= GRAVITY_PX * dt;
      this.jumpY  += this.jumpVy * dt;
      if (this.jumpY <= 0) {
        this.jumpY = 0;
        this.jumpVy = 0;
        this.playerEl.classList.remove('jumping');
      }
    }
    const min = PLAYER_W / 2 + 10;
    const max = this.viewportW - PLAYER_W / 2 - 10;
    if (this.playerX < min) this.playerX = min;
    if (this.playerX > max) this.playerX = max;
    this.placePlayer();
    // Toggle the .moving class so the wobble CSS animation cranks up
    // while keys are held. Driven from updatePlayer so the visual
    // immediately matches the input state. Dash counts as moving so
    // the puppet leans into the burst.
    const moving = this.leftHeld || this.rightHeld || dashing;
    this.playerEl.classList.toggle('moving', moving);
  }

  private updateFlies(dt: number) {
    for (const fly of this.flies) {
      if (fly.dead) continue;
      fly.age += dt;

      switch (fly.motion) {
        case 'straight':
          fly.y += fly.vy * dt;
          break;
        case 'sine':
          fly.y += fly.vy * dt;
          fly.x = fly.baseX + Math.sin(fly.age * 3.2) * 90;
          break;
        case 'random':
          fly.y += fly.vy * dt;
          fly.x += fly.vx * dt;
          if (fly.x < fly.size / 2) {
            fly.x = fly.size / 2;
            fly.vx = -fly.vx;
          } else if (fly.x > this.viewportW - fly.size / 2) {
            fly.x = this.viewportW - fly.size / 2;
            fly.vx = -fly.vx;
          }
          break;
      }

      const tx = fly.x - fly.size / 2;
      const ty = fly.y - fly.size / 2;
      const rot = fly.age * fly.spinRate * fly.spinDir;
      fly.el.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg)`;

      if (fly.y > this.viewportH + fly.size) {
        fly.dead = true;
        fly.el.remove();
      }
    }
  }

  private checkPlayerHit(now: number): boolean {
    // Player's full visual bounding box (no padding shrink — the
    // mask handles transparency on its own). Box left/top/right/
    // bottom are the screen-space rect that the greyman PNG occupies.
    const boxLeft = this.playerX - PLAYER_W / 2;
    // boxTop tracks jumpY so collisions follow the puppet through the
    // air — without this the silhouette mask would still be tested
    // against the ground position even mid-jump.
    const boxTop = this.viewportH - PLAYER_H - PLAYER_BOTTOM_OFFSET - this.jumpY;
    const boxRight = boxLeft + PLAYER_W;
    const boxBottom = boxTop + PLAYER_H;

    for (const fly of this.flies) {
      if (fly.dead) continue;

      // Cheap bbox early-out: skip the mask sampling when the fly
      // can't possibly overlap the player's rect.
      const fl = fly.x - fly.size / 2;
      const fr = fly.x + fly.size / 2;
      const ft = fly.y - fly.size / 2;
      const fb = fly.y + fly.size / 2;
      if (fr <= boxLeft || fl >= boxRight || fb <= boxTop || ft >= boxBottom) continue;

      let hit = false;
      if (this.playerMask) {
        // Sample 5 points around the fly's center against the
        // pixel-precise silhouette: center + four cardinal points
        // at ~70% of the fly's radius. If any point falls on a
        // non-transparent player pixel, count as collision. Five
        // samples is the right spot on the speed/accuracy curve —
        // it catches glancing hits without overpaying for full
        // shape-vs-shape intersection.
        const r = fly.size * 0.35;
        const samples: Array<[number, number]> = [
          [fly.x,       fly.y      ],
          [fly.x - r,   fly.y      ],
          [fly.x + r,   fly.y      ],
          [fly.x,       fly.y - r  ],
          [fly.x,       fly.y + r  ],
        ];
        for (const [sx, sy] of samples) {
          if (this.playerMask.isOpaqueAt(sx, sy, boxLeft, boxTop, PLAYER_W, PLAYER_H)) {
            hit = true;
            break;
          }
        }
      } else {
        // Fallback while the mask is still loading: shrink the rect
        // by the legacy pad values so it's not too generous.
        const pl = boxLeft + PLAYER_HIT_PAD_X;
        const pr = boxRight - PLAYER_HIT_PAD_X;
        const pt = boxTop + PLAYER_HIT_PAD_Y;
        const pb = boxBottom - PLAYER_HIT_PAD_Y;
        if (fr > pl && fl < pr && fb > pt && ft < pb) hit = true;
      }

      if (hit) {
        this.engine.loseLife();
        this.invulnUntil = now + INVULN_MS;
        this.flashScreen();
        this.flashPlayer();
        fly.dead = true;
        fly.el.remove();
        return true;
      }
    }
    return false;
  }

  // ── Spawning ────────────────────────────────────────────────────

  private spawnFly(stage: StageConfig) {
    const type = stage.flyTypes[Math.floor(Math.random() * stage.flyTypes.length)];
    const size = randInt(stage.flySize.min, stage.flySize.max);
    const x = randInt(size / 2 + 10, this.viewportW - size / 2 - 10);
    const y = -size;
    const vy = randInt(stage.flySpeedPx.min, stage.flySpeedPx.max);
    const vx = stage.flyMotion === 'random' ? randInt(-220, 220) : 0;

    const el = document.createElement('img');
    el.className = 'arena-fly';
    el.src = `/img/fly${type}.png`;
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.draggable = false;
    el.alt = '';
    // Append to the dedicated fly layer (behind back-curtains), not
    // the .arena root (which is in front of them).
    this.flyLayer.appendChild(el);

    this.flies.push({
      el,
      x, y, vx, vy, size,
      spinDir: Math.random() > 0.5 ? 1 : -1,
      spinRate: randInt(120, 360), // deg/sec
      baseX: x,
      motion: stage.flyMotion,
      age: 0,
      dead: false,
    });
  }

  private clearAllFlies() {
    for (const fly of this.flies) {
      if (!fly.dead) {
        fly.dead = true;
        fly.el.remove();
      }
    }
    this.flies = [];
  }

  // ── FX ──────────────────────────────────────────────────────────

  private flashScreen() {
    this.flashEl.classList.remove('flashing');
    void this.flashEl.offsetWidth;
    this.flashEl.classList.add('flashing');
  }
  private flashPlayer() {
    this.playerEl.classList.remove('hit');
    void this.playerEl.offsetWidth;
    this.playerEl.classList.add('hit');
    setTimeout(() => this.playerEl.classList.remove('hit'), 700);
  }

  // ── Cleanup ─────────────────────────────────────────────────────

  private cleanup() {
    this.active = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.removeInput();
    this.clearAllFlies();
    this.clearBeam();
    if (this.bossSpriteEl) {
      this.bossSpriteEl.remove();
      this.bossSpriteEl = null;
    }
    if (this.bossHpWrapEl) {
      this.bossHpWrapEl.remove();
      this.bossHpWrapEl = null;
    }
    this.bossHpEl = null;
    this.root.classList.remove('visible');
    // Restore back-curtains z-index right away — the visual "puppet
    // behind drapes" effect is no longer needed once the arena UI
    // is fading out, and other scenes assume the original z-1.
    if (this.backCurtainsEl) {
      this.backCurtainsEl.style.zIndex = this.savedBackCurtainsZ;
      this.backCurtainsEl.style.clipPath = this.savedBackCurtainsClip;
      this.backCurtainsEl = null;
    }
    // Restore audience to its pre-arena lowered/raised state.
    if (this.audienceEl) {
      if (!this.audienceWasLowered) this.audienceEl.classList.remove('lowered');
      this.audienceEl = null;
    }
    setTimeout(() => {
      if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
      if (this.flyLayer?.parentNode) this.flyLayer.parentNode.removeChild(this.flyLayer);
      if (this.playerLayer?.parentNode) this.playerLayer.parentNode.removeChild(this.playerLayer);
    }, 700);
  }

  // ── Helpers ─────────────────────────────────────────────────────

  private wait(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}

function randInt(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}
